import Vue from 'vue'
import App from './App.vue'
import createRouter from './router'
import createStore from './store'
const placeholderimage = require('@/assets/placeholder.png')
import VueLazyload from 'vue-lazyload'
import global from '@/utils/global'
import { initializeApp } from 'firebase/app'
import { getAnalytics, logEvent, isSupported } from 'firebase/analytics'
import VueMeta from 'vue-meta'
import firebaseConfig from '../firebaseConfig' // 引入Firebase配置
import { callAndroidBridge, callStatsEventWithRetry, createAdTelemetryV2, createStatsExtra, createStatsPvId } from '@/utils/adTelemetryV2'

Vue.prototype.$global = global

Vue.use(VueMeta)

Vue.use(VueLazyload, {
	preLoad: 1.3,
	error: placeholderimage,
	loading: placeholderimage,
	attempt: 1,
})

// 队列用于在 Analytics 初始化之前暂存事件
const eventQueue = []

let analyticsInstance = null
let adTelemetryV2 = null

const getPageParams = () => {
	if (typeof window === 'undefined') return {}
	return {
		path: window.location.pathname,
		search: window.location.search,
		title: document.title,
		referrer: document.referrer || '',
	}
}

// 默认的 $logEvent 和 $eventrack 方法，将请求放入队列
Vue.prototype.$logEvent = (event, params = {}) => {
	console.log(`Queued log: ${event}`, params)
	eventQueue.push({ type: 'log', event, params })
}
Vue.prototype.$eventrack = (msg, method, map = {}) => {
	console.log(`Queued track: ${msg}`, method, map)
	eventQueue.push({ type: 'track', msg, method, map })
}
Vue.prototype.$trackInPage = (params = {}) => {
	eventQueue.push({ type: 'in_page', params: { ...getPageParams(), ...params } })
}
Vue.prototype.$statsEvent = () => false

function initializeFirebase() {
	const fconfig = window?.__INITIAL_STATE__?.domainConfig?.firebase || firebaseConfig
	const app = initializeApp(fconfig)
	const analytics = getAnalytics(app)
	return analytics
}

function trackClientStatsEvent(eventName, extraParams = {}) {
	if (typeof window === 'undefined') return ''

	const statsPvId = createStatsPvId()
	const statsExtra = createStatsExtra(statsPvId, extraParams)
	callStatsEventWithRetry(eventName, window.location.href, statsExtra)
	return statsPvId
}

function sendFirebaseEvent(event, params = {}) {
	if (analyticsInstance) {
		logEvent(analyticsInstance, event, params)
		return
	}
	eventQueue.push({ type: 'log', event, params })
}

function trackInPage(eventParams = {}) {
	if (typeof window === 'undefined') return

	const statsPvId = trackClientStatsEvent('in_page')
	const params = {
		...getPageParams(),
		...eventParams,
		stats_pv_id: statsPvId,
	}

	if (analyticsInstance) {
		logEvent(analyticsInstance, 'in_page', params)
		console.log('in_page', params)
	} else {
		eventQueue.push({ type: 'in_page', params })
	}
}

function flushEventQueue() {
	while (eventQueue.length > 0) {
		const queuedEvent = eventQueue.shift()
		if (queuedEvent.type === 'log') {
			sendFirebaseEvent(queuedEvent.event, queuedEvent.params)
		} else if (queuedEvent.type === 'track') {
			const params = {
				time: new Date(),
				message: queuedEvent.msg,
				method: queuedEvent.method,
				...queuedEvent.map,
			}
			sendFirebaseEvent(queuedEvent.msg, params)
		} else if (queuedEvent.type === 'in_page' && analyticsInstance) {
			logEvent(analyticsInstance, 'in_page', queuedEvent.params)
		}
	}
}

function setupAndroidBridgePrototypes() {
	Vue.prototype.$statsEvent = (eventName, extraParams = {}) => trackClientStatsEvent(eventName, extraParams)
	Vue.prototype.$adTelemetryV2 = (eventType = 'init', triggerSource = 'manual', extra = {}) => {
		if (!adTelemetryV2) return false
		return adTelemetryV2.report(eventType, triggerSource, extra)
	}
	Vue.prototype.$adClick = (message) => {
		if (!callAndroidBridge('adClick', message)) {
			console.log(message)
		}
		let extra = {}
		try {
			extra = JSON.parse(message)
		} catch (error) {
			extra = { raw: message }
		}
		Vue.prototype.$adTelemetryV2('click', 'legacy_adClick', extra)
	}
	Vue.prototype.$adPosition = (message) => {
		if (!callAndroidBridge('adPosition', message)) {
			console.log(message)
		}
	}
	Vue.prototype.$trackInPage = trackInPage
}

function setupAdTelemetryV2() {
	if (typeof window === 'undefined' || adTelemetryV2) return

	adTelemetryV2 = createAdTelemetryV2()
	const start = () => adTelemetryV2 && adTelemetryV2.init()
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', start, { once: true })
	} else {
		setTimeout(start, 0)
	}
}

function setupAnalytics() {
	isSupported().then((result) => {
		setupAndroidBridgePrototypes()
		if (result) {
			analyticsInstance = initializeFirebase()
			trackInPage()

			// 覆盖默认的上报方法
			Vue.prototype.$logEvent = (event, params = {}) => {
				console.log(event)
				sendFirebaseEvent(event, params)
			}
			Vue.prototype.$eventrack = (msg, method, map = {}) => {
				const params = {
					time: new Date(),
					message: msg,
					method: method,
					...map,
				}
				console.log(msg)
				sendFirebaseEvent(msg, params)
			}

			// 处理队列中的所有请求
			flushEventQueue()
		} else {
			console.log('Firebase Analytics not supported')
			trackClientStatsEvent('in_page')
			eventQueue.length = 0
		}
	}).catch((error) => {
		console.error('Firebase Analytics setup failed', error)
		setupAndroidBridgePrototypes()
		trackClientStatsEvent('in_page')
	})
}

if (typeof window !== 'undefined') {
	setupAdTelemetryV2()
	setupAnalytics()
} else {
	// 服务器端只定义简单的 log
	Vue.prototype.$logEvent = (event, params = {}) => {
		console.log(`Server Log: ${event}`, params)
	}
	Vue.prototype.$eventrack = (event, params = {}) => {
		console.log(`Server Log: ${event}`, params)
	}
	Vue.prototype.$adClick = (msg) => {
		console.log(`Server Log: ${msg}`)
	}
	Vue.prototype.$adPosition = (msg) => {
		console.log(`Server Log: ${msg}`)
	}
	Vue.prototype.$adTelemetryV2 = () => false
	Vue.prototype.$trackInPage = () => {}
	Vue.prototype.$statsEvent = () => false
}

export default function createApp() {
	const router = createRouter()
	const store = createStore()
	if (typeof window !== 'undefined') {
		router.afterEach(() => {
			setTimeout(() => {
				Vue.prototype.$trackInPage?.()
				Vue.prototype.$adTelemetryV2?.('init', 'route_after_each')
			}, 120)
		})
	}
	const app = new Vue({
		router,
		store,
		render: (h) => h(App),
	})
	return {
		app,
		router,
		store,
	}
}
